import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import PageError from '../../../../../../shared/components/general/PageError';
import MedicalIntegrationConfigContainer from '../../../../../../shared/hooks/MedicalIntegrationConfigContainer';
import PmsLookupContainer from '../../../../../../shared/hooks/PmsLookupContainer';
import MedicalIntegrationConfigSettings, { Header, PmsBadge, EmptyState, TextLinkButton, getPmsDisplayName } from '../../../../../../shared/scenes/organizationSettings/components/MedicalIntegrationConfigSettings';
import { normalizePmsName } from '../../../../../../shared/constants/sparkConstants/dentalPms';
import { silver_two, color_shades_darker, color_colors_ocean } from '../../../../../../shared/constants/colors';
import {
    ProcedureTypesLookup,
    AvailableSlotsLookup,
    PatientsLookup,
    OfficePicker
} from './PmsLookupPanels';
import { SearchPicker } from './SearchPicker';
import ProcedureTypesProposal from './ProcedureTypesProposal';
import ConfigConnectionStatus from './ConfigConnectionStatus';
import OfficeConnectionStatus, { LocationFormValues } from './OfficeConnectionStatus';
import { CancellationCodesLookup, ReferralTypesLookup } from './SparkFormCodeLookup';
import { ClearPmsCacheButton, ClearPmsCacheStatus, useClearPmsCache } from './ClearPmsCacheButton';
import { Cloud9ProvidersLookup, ProvidersLookup } from './SparkFormProviderLookups';
import { needsConnection, useConfigIdFieldRows } from './configConnection';
import ApplicationModel from '../../../../../../shared/models/applications/api/ApplicationModel';

interface PmsManagerSectionProps {
    organizationId: string;
    // Location apps plus the Shared Configuration app, for saving proposed procedure types
    locationApps?: ApplicationModel[];
    // Each location's effective dental form values, already loaded by the Location Manager
    formValues?: { [appId: string]: LocationFormValues };
    // Called after a Spark form field is saved so the parent can reload that app's values
    onFormSaved?: (appId: string) => void;
}

type PmsManagerSubtab = 'status' | 'configurations' | 'lookups';

// More PMS tools can be added here as new subtabs
const SUBTABS: { id: PmsManagerSubtab; label: string }[] = [
    { id: 'status', label: 'Office Connection' },
    { id: 'configurations', label: 'Configurations' },
    { id: 'lookups', label: 'Lookups' }
];

type LookupTab = 'procedure-types' | 'providers' | 'slots' | 'patients' | 'cancellation-codes' | 'referral-types';

interface LookupTabOption {
    id: LookupTab;
    label: string;
}

const SUPPORTED_PMS = ['denticon', 'cloud-9', 'dentrix-ascend', 'open-dental'];

const getLookupTabs = (pmsName: string): LookupTabOption[] => {
    const tabs: LookupTabOption[] = [
        { id: 'procedure-types', label: 'Procedure Types' },
        { id: 'providers', label: pmsName === 'cloud-9' ? 'Provider IDs' : 'Providers' },
        { id: 'slots', label: 'Available Slots' },
        { id: 'patients', label: 'Patients' }
    ];
    if (pmsName === 'denticon') {
        tabs.push({ id: 'cancellation-codes', label: 'Cancellation Codes' });
        tabs.push({ id: 'referral-types', label: 'Referral Types' });
    }
    return tabs;
};

const PmsManagerContent: React.FC<PmsManagerSectionProps> = ({ organizationId, locationApps: unsortedApps = [], formValues = {}, onFormSaved }) => {
    // Apps are listed alphabetically everywhere in the PMS Manager
    const locationApps = useMemo(
        () => [...unsortedApps].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        [unsortedApps.map(app => `${app.id}:${app.name}`).join(',')]
    );
    const medicalConfigContainer = MedicalIntegrationConfigContainer.useContainer();
    const pms = PmsLookupContainer.useContainer();
    const [activeSubtab, setActiveSubtab] = useState<PmsManagerSubtab>('status');
    const [activeTab, setActiveTab] = useState<LookupTab>('procedure-types');
    const [hasLoadedConfigs, setHasLoadedConfigs] = useState(false);
    // PMS lookups are billed per call, so nothing is pulled from the PMS until the Lookups tab is opened
    const [hasOpenedLookups, setHasOpenedLookups] = useState(false);
    const wasLoadingConfigs = useRef(false);

    const configs = medicalConfigContainer.medicalConfigs.filter(config => config && config.id);
    // Loaded as soon as the section opens: it decides the default tab and shows on Configurations
    const connection = useConfigIdFieldRows(locationApps);
    const needsSetup = hasLoadedConfigs && connection.hasLoaded && (configs.length === 0 || needsConnection(connection.rows, configs));
    const selectedConfig = configs.find(config => config.id === pms.configId) ?? null;
    const sortedConfigs = useMemo(() => [...configs].sort((a, b) => (a.clientName || '').localeCompare(b.clientName || '')), [configs]);
    const pmsName = selectedConfig ? normalizePmsName(selectedConfig.integrationType || '') : '';
    const isSupported = SUPPORTED_PMS.includes(pmsName);
    const tabs = getLookupTabs(pmsName);

    // Configurations load from the (hidden) Configurations tab on mount; note when the first load finishes
    useEffect(() => {
        if (wasLoadingConfigs.current && !medicalConfigContainer.isLoading) {
            setHasLoadedConfigs(true);
        }
        wasLoadingConfigs.current = medicalConfigContainer.isLoading;
    }, [medicalConfigContainer.isLoading]);

    // Select the first configuration once they load so lookups are ready without an extra click
    useEffect(() => {
        if (!pms.configId && configs.length > 0) {
            pms.selectConfig(configs[0].id);
        } else if (pms.configId && !selectedConfig && !medicalConfigContainer.isLoading) {
            pms.selectConfig(configs[0]?.id ?? null);
        }
    }, [configs.map(config => config.id).join(','), medicalConfigContainer.isLoading]);

    useEffect(() => {
        if (activeSubtab === 'lookups') {
            setHasOpenedLookups(true);
        }
    }, [activeSubtab]);

    useEffect(() => {
        setActiveTab('procedure-types');
    }, [pms.configId]);

    // Offices drive most lookups, so fetch them once Lookups is open and a configuration is selected
    useEffect(() => {
        if (hasOpenedLookups && selectedConfig && isSupported) {
            pms.loadOffices();
        }
    }, [pms.configId, hasOpenedLookups]);

    // An edited configuration keeps its ID, so its cached results and offices describe the old settings
    const selectedConfigVersion = selectedConfig ? `${selectedConfig.id}|${selectedConfig.integrationType}|${selectedConfig.configuration}` : '';
    const lastConfigVersion = useRef(selectedConfigVersion);
    useEffect(() => {
        const previous = lastConfigVersion.current;
        lastConfigVersion.current = selectedConfigVersion;
        const sameConfig = !!selectedConfig && previous.split('|')[0] === selectedConfig.id;
        if (!sameConfig || previous === selectedConfigVersion) return;
        pms.clearConfig();
        if (hasOpenedLookups && isSupported) {
            pms.loadOffices(true);
        }
    }, [selectedConfigVersion]);

    const handleFormSaved = (appId: string) => onFormSaved?.(appId);

    // After clearing the server's office list (or everything), pull a fresh one from the PMS. Other categories only
    // reset their own results, which reload from the PMS when their tab is next shown.
    const handleCacheCleared = (type?: string) => {
        if (isSupported && (!type || type === 'offices')) {
            pms.loadOffices(true);
        }
    };
    const cacheClear = useClearPmsCache(selectedConfig?.clientName || 'this configuration', handleCacheCleared);

    // A single office is the only choice, so pick it
    useEffect(() => {
        const offices = pms.offices.data ?? [];
        if (!pms.officeId && offices.length === 1) {
            pms.setOfficeId(offices[0].id);
        }
    }, [pms.offices.data]);

    const renderLookup = () => {
        switch (activeTab) {
            case 'procedure-types': return (
                <ProcedureTypesLookup
                    renderTypes={types => <ProcedureTypesProposal pmsProcedureTypes={types} officeId={pms.officeId} locationApps={locationApps} onSaved={handleFormSaved} />}
                />
            );
            case 'providers': return pmsName === 'cloud-9'
                ? <Cloud9ProvidersLookup locationApps={locationApps} officeId={pms.officeId} onFormSaved={handleFormSaved} />
                : <ProvidersLookup locationApps={locationApps} officeId={pms.officeId} onFormSaved={handleFormSaved} />;
            // Keyed by configuration and office so the picked procedure type, provider and schedule view reset with them
            case 'slots': return <AvailableSlotsLookup key={`${pms.configId}|${pms.officeId}`} isCloud9={pmsName === 'cloud-9'} />;
            case 'patients': return <PatientsLookup pmsName={pmsName} />;
            case 'cancellation-codes': return <CancellationCodesLookup locationApps={locationApps} officeId={pms.officeId} onFormSaved={handleFormSaved} />;
            case 'referral-types': return <ReferralTypesLookup locationApps={locationApps} officeId={pms.officeId} onFormSaved={handleFormSaved} />;
        }
    };

    const currentSubtab = activeSubtab;

    const lookupsPanel = () => {
        if (configs.length === 0) {
            return (
                <EmptyState>
                    Add a PMS configuration to start pulling data.{' '}
                    <TextLinkButton onClick={() => setActiveSubtab('configurations')}>Go to Configurations</TextLinkButton>
                </EmptyState>
            );
        }
        if (!selectedConfig) {
            return null;
        }
        return (
            <>
                <Header>
                    {configs.length > 1 ? (
                        // Searchable so orgs with ~20 configurations (e.g. Smile Doctors) stay manageable
                        <SearchPicker
                            items={sortedConfigs}
                            selectedId={pms.configId}
                            getId={config => config.id}
                            selectedLabel={config => `${config.clientName} · ${getPmsDisplayName(config.integrationType)}`}
                            searchText={config => [config.clientName, getPmsDisplayName(config.integrationType), config.id]}
                            renderItem={config => <><span>{config.clientName}</span><PmsBadge>{getPmsDisplayName(config.integrationType)}</PmsBadge></>}
                            onSelect={config => pms.selectConfig(config.id)}
                            placeholder="Search configurations"
                            ariaLabel="Configuration"
                            noun="configurations"
                            width={380}
                        />
                    ) : (
                        <LookupHeading>
                            <ClientName>{selectedConfig.clientName}</ClientName>
                            <PmsBadge>{getPmsDisplayName(selectedConfig.integrationType)}</PmsBadge>
                        </LookupHeading>
                    )}
                    <HeaderActions>
                        {isSupported && (
                            <OfficePicker
                                offices={pms.offices.data ?? []}
                                selectedOfficeId={pms.officeId}
                                isLoading={pms.offices.isLoading}
                                onSelect={pms.setOfficeId}
                            />
                        )}
                        <ClearPmsCacheButton isClearing={cacheClear.isClearing} onClear={cacheClear.clear} />
                    </HeaderActions>
                </Header>
                <ClearPmsCacheStatus message={cacheClear.message} errors={cacheClear.errors} />

                {!isSupported ? (
                    <PageError type="warning" errors={[`Lookups aren't available for "${selectedConfig.integrationType}". Supported systems are Denticon, Cloud 9, Dentrix Ascend and Open Dental.`]} />
                ) : (
                    <>
                        <PageError errors={pms.offices.errors.map(error => `Couldn't load offices: ${error}`)} />
                        <ChipBar role="tablist" aria-label="Lookup type">
                            {tabs.map(tab => (
                                <Chip
                                    key={tab.id}
                                    role="tab"
                                    aria-selected={activeTab === tab.id}
                                    active={activeTab === tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    {tab.label}
                                </Chip>
                            ))}
                        </ChipBar>
                        <div role="tabpanel">
                            {renderLookup()}
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <Container>
            <TabBar role="tablist" aria-label="PMS Manager">
                {SUBTABS.map(subtab => (
                    <Tab
                        key={subtab.id}
                        role="tab"
                        aria-selected={currentSubtab === subtab.id}
                        active={currentSubtab === subtab.id}
                        onClick={() => setActiveSubtab(subtab.id)}
                    >
                        {subtab.id === 'configurations' && hasLoadedConfigs ? `${subtab.label} (${configs.length})` : subtab.label}
                        {subtab.id === 'configurations' && needsSetup && <SetupDot title="Needs setup" />}
                    </Tab>
                ))}
            </TabBar>

            <div role="tabpanel" hidden={currentSubtab !== 'status'}>
                <OfficeConnectionStatus locationApps={locationApps} formValues={formValues} />
            </div>
            {/* Kept mounted while hidden so configurations load once and survive tab switches */}
            <div role="tabpanel" hidden={currentSubtab !== 'configurations'}>
                <MedicalIntegrationConfigSettings
                    organizationId={organizationId}
                    title="Saved configurations"
                    renderConfigActions={locationApps.length > 0 ? config => (
                        <ConfigConnectionStatus config={config} rows={connection.rows} isLoading={connection.isLoading} onSaved={(appId, configId) => { connection.setRowValue(appId, configId); handleFormSaved(appId); }} />
                    ) : undefined}
                />
            </div>
            <div role="tabpanel" hidden={currentSubtab !== 'lookups'}>
                {/* Mounted on first open: the lookup panels load PMS data as soon as they mount */}
                {!hasOpenedLookups ? null : hasLoadedConfigs ? lookupsPanel() : <EmptyState>Loading configurations...</EmptyState>}
            </div>
        </Container>
    );
};

const PmsManagerSection: React.FC<PmsManagerSectionProps> = (props) => {
    return (
        <MedicalIntegrationConfigContainer.Provider>
            <PmsLookupContainer.Provider>
                <PmsManagerContent {...props} />
            </PmsLookupContainer.Provider>
        </MedicalIntegrationConfigContainer.Provider>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: flex-end;
`;

const LookupHeading = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const ClientName = styled.span`
    font-family: Muli;
    font-size: 13px;
    color: ${color_shades_darker};
`;

const TabBar = styled.div`
    display: flex;
    gap: 24px;
    border-bottom: 1px solid ${silver_two};
    margin: 0 0 24px 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const Tab = styled.button<{ active: boolean }>`
    background: none;
    border: none;
    border-bottom: 2px solid ${props => props.active ? color_colors_ocean : 'transparent'};
    margin-bottom: -1px;
    padding: 8px 0;
    font-family: Muli;
    font-size: 13px;
    font-weight: ${props => props.active ? 700 : 400};
    color: ${props => props.active ? color_colors_ocean : color_shades_darker};
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        color: ${color_colors_ocean};
    }
`;

const SetupDot = styled.span`
    display: inline-block;
    width: 7px;
    height: 7px;
    margin-left: 6px;
    border-radius: 50%;
    background-color: #d69e2e;
    vertical-align: middle;
`;

const ChipBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 12px 0 20px 0;
`;

const Chip = styled.button<{ active: boolean }>`
    height: 30px;
    padding: 0 14px;
    border: 1px solid ${props => props.active ? color_colors_ocean : silver_two};
    border-radius: 15px;
    background-color: ${props => props.active ? 'rgba(46, 125, 206, 0.08)' : '#ffffff'};
    font-family: Muli;
    font-size: 13px;
    font-weight: ${props => props.active ? 700 : 400};
    color: ${props => props.active ? color_colors_ocean : '#4a5568'};
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease-in-out;

    &:hover {
        border-color: ${color_colors_ocean};
        color: ${color_colors_ocean};
    }
`;

export default PmsManagerSection;
